import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Divider,
  Button,
  Icon,
  Grid,
  Switch,
  FormHelperText
} from '@material-ui/core';
import { changeAboutUsPageConfigConfig } from 'redux/actions/store_configs';
import { BackdropCircular, ImageGalleryDialog } from 'components';
import { assetManagerUrl } from 'configs';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadImageResolutionLimit
} from 'redux/actions/general';
import {
  uploadPrivateImageApi,
  getAssetManagerImagesApi
} from 'api';
import SelectOwnImage from './SelectOwnImage';


const AboutUsPageBanner = ({values, onChange}) => {
  const dispatch = useDispatch();
  const [openImageDialog, toggleOpenImageDialog] = useState(false);
  const [ownImagesTagIds, setOwnImagesTagIds] = useState('');

  const { page_top_banner } = values;
  const {
    assetCategoryTags,
    currentCategoryId,
    assetImagesData,
    uploadingPrivateImage
  } = useSelector((state: IReduxState) => state.asset_manager);

  const {profile: {stores = []}} = useSelector((state: IReduxState) => state.user);

  const storeCode = stores.length && stores[0]?.storeCode;
  const {id: tagId, tagCode} = assetCategoryTags.find((tag) => tag.tagCode === 'about_us_page_top_banner') ||
                               {id: null, tagCode: null};
  const privateTagData = assetCategoryTags.find((tag) => tag.tagCode === 'private');
  const storeCodeTagData = assetCategoryTags.find((tag) => tag.tagCode === `${storeCode}`);

  useEffect(() => {
    if (tagId && privateTagData && storeCodeTagData) {
      setOwnImagesTagIds(`${tagId}, ${privateTagData.id}, ${storeCodeTagData.id}`);
    }
  }, [tagId, privateTagData, storeCodeTagData]);

  useEffect(() => {
    if (ownImagesTagIds.length) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: ownImagesTagIds,
        isPrivate: true
      });
    }
  }, [ownImagesTagIds]);

  const handleChange = (event) => {
    const {name, checked, type, value} = event.target;
    dispatch(changeAboutUsPageConfigConfig(name, checked, type, value, 'page_top_banner'));
  };

  const handleSelectImage = (data, selectImageConfigPath) => {
    const value = `${assetManagerUrl}/${data.webPath}`;
    dispatch(changeAboutUsPageConfigConfig(
      selectImageConfigPath,
      null,
      'text',
      value,
      'page_top_banner'
    ));
    onChange(`page_top_banner[${selectImageConfigPath}]`, value);
  };

  const handleUpLoadImage = async (event) => {
    event.persist();
    const {files, value} = event.target;
    const formData = new FormData();

    if (files.length) {
      const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
      const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
      const acceptableFormats = 'png|jpeg';
      const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

      const reader = new FileReader();

      reader.readAsDataURL(files[0]);
      reader.onload = (e) => {
        const image = new Image();

        image.src = e.target.result as string;
        image.onload = async () => {
          const {height, width} = image;

          if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
            dispatch(uploadFileTypeUnsupported());
          } else if (width !== 1660 && height !== 500) {
            dispatch(uploadImageResolutionLimit('1660 x 500px'));
          } else if (files[0].size > 524288) {
            dispatch(uploadFileSizeLimit('512kB'));
          } else {
            formData.append('image', files[0]);

            if (ownImagesTagIds.length) {
              formData.append('tags', `${tagCode},private,${storeCode}`);
            } else {
              const tags = [
                tagCode,
                privateTagData.tagCode,
                {
                  label: storeCode,
                  tagCode: storeCode,
                  category: currentCategoryId
                }
              ];
              formData.append('tags', JSON.stringify(tags));
            }

            const imageData = await uploadPrivateImageApi({dispatch, formData, isAddingNewTag: !ownImagesTagIds.length});

            if (imageData) {
              handleSelectImage(imageData, 'store_info/about_us_page/top_banner_image');
            }
          }
        };
      };
    }
  };

  return (
    <div>
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
        <Grid item xs={12} sm={9}>
          <Switch
            color="primary"
            name={'store_info/about_us_page/is_top_banner_enabled'}
            checked={page_top_banner['store_info/about_us_page/is_top_banner_enabled'] == 1}
            onChange={(event) => {
              onChange('page_top_banner[\'store_info/about_us_page/is_top_banner_enabled\']', event.target.checked ? 1 : 0);
              handleChange(event);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Banner Image</Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="p-0 w-full max-w-300 text-center"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label
                htmlFor="upload-provider-image"
                className="flex items-center justify-center cursor-pointer w-full px-5 py-2"
              >
                <Icon className="mr-2">upload</Icon>
                Upload Own Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={handleUpLoadImage}
              onClick={(event) => (event.target as HTMLInputElement).value = null}
              id="upload-provider-image"
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            Files supported: png, jpeg, jpg. File size should be less than 512kB
          </FormHelperText>
          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => toggleOpenImageDialog(true)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Library
          </Button>
          <SelectOwnImage
            title="Select Banner Image From Own Images"
            imagesTagIds={ownImagesTagIds}
            isOwnImages={assetImagesData?.[ownImagesTagIds]?.items?.length > 0}
            selectImageConfigPath="store_info/about_us_page/top_banner_image"
            handleSelectImage={handleSelectImage}
            cols={3}
            rowHeight={90}
          />
        </Grid>
      </Grid>
      {tagId &&
        <ImageGalleryDialog
          title="Select Top Banner Image"
          open={openImageDialog}
          setOpen={toggleOpenImageDialog}
          onImageSelect={(data) => handleSelectImage(data, 'store_info/about_us_page/top_banner_image')}
          imageTagId={tagId}
          cols={3}
          rowHeight={90}
        />
      }
      <Divider className="my-8" />
      <BackdropCircular loading={uploadingPrivateImage} />
    </div>
  );
};

export default AboutUsPageBanner;
